import { z } from 'zod'
import { apiFetch } from './client'

// Schema matching your backend Location model
export const LocationSchema = z.object({
  id: z.number(),
  location_id: z.string(), // Google's location identifier (e.g., "locations/123456789" or place_id)
  name: z.string(),
  display_name: z.string().nullable().optional(), // User-customized display name
  provider: z.string(), // "GOOGLE" or other providers
  location_type: z.string().optional(), // "owned", "competitor", "benchmark"
  address: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  category: z.string().nullable(),
  is_tracked: z.boolean(),
  created_at: z.string(),
  // Optional fields that may not always be present
  account_id: z.number().optional(),
  store_code: z.string().optional(),
  updated_at: z.string().optional(),
  google_maps_url: z.string().optional(), // Google Maps URL for review syncing
  rating: z.number().nullable().optional(),
  reviews_count: z.number().nullable().optional(),
  trend: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
})

export type Location = z.infer<typeof LocationSchema>

/**
 * Sync locations for a specific account from Google Business Profile
 */
export async function syncLocationsForAccount(
  accountId: number
): Promise<{ synced: number; locations: Location[] }> {
  const data = await apiFetch<{
    account_id: number
    locations_synced: number
    locations: Location[]
  }>(`/locations/accounts/${accountId}/sync`, { method: 'POST' })

  return {
    synced: data.locations_synced,
    locations: z.array(LocationSchema).parse(data.locations),
  }
}

/**
 * Fetch all locations for a user (across all their accounts)
 */
export async function fetchUserLocations(userId: string): Promise<Location[]> {
  const data = await apiFetch<Location[]>(`/locations/users/${userId}`)
  return z.array(LocationSchema).parse(data)
}

/**
 * Enable/disable tracking for a location
 */
export async function updateLocationTracking(
  locationId: number,
  isTracked: boolean
): Promise<Location> {
  const data = await apiFetch<Location>(`/locations/${locationId}/tracking`, {
    method: 'PATCH',
    body: JSON.stringify({ is_tracked: isTracked }),
  })
  return LocationSchema.parse(data)
}

/**
 * Update location details (display name, etc.)
 */
export async function updateLocation(
  locationId: number,
  updates: { display_name?: string | null }
): Promise<Location> {
  const data = await apiFetch<Location>(`/locations/${locationId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
  return LocationSchema.parse(data)
}

/**
 * Permanently delete a location and all of its associated data.
 */
export async function deleteLocation(locationId: number): Promise<void> {
  await apiFetch(`/locations/${locationId}`, { method: 'DELETE' })
}

/**
 * Batch update tracking for multiple locations
 */
export async function batchUpdateLocationTracking(
  locationIds: number[],
  isTracked: boolean
): Promise<{ updated: number }> {
  // Update each location individually (backend doesn't have batch endpoint yet)
  const promises = locationIds.map((id) => updateLocationTracking(id, isTracked))
  await Promise.all(promises)

  return { updated: locationIds.length }
}

/**
 * Response from creating an internal location
 */
export const CreateInternalLocationResponseSchema = z.object({
  location: LocationSchema,
  created: z.boolean(),
  review_count: z.number(),
})

export type CreateInternalLocationResponse = z.infer<typeof CreateInternalLocationResponseSchema>

/**
 * Create an internal location (for users not using Google OAuth)
 * This stores location data from Google Places search
 *
 * If review_count > 0, the location already has reviews from a previous sync
 * and the caller should skip triggering a new sync.
 */
export async function createInternalLocation(placeData: {
  name: string
  address: string
  googleMapsUrl: string
  placeId: string
  phone?: string
  website?: string
  latitude?: number
  longitude?: number
  /** Google Places types, most-specific first. Records what kind of business this is. */
  types?: string[]
}): Promise<CreateInternalLocationResponse> {
  const data = await apiFetch<CreateInternalLocationResponse>(`/locations/internal`, {
    method: 'POST',
    body: JSON.stringify({
      location_id: placeData.placeId,
      name: placeData.name,
      provider: 'GOOGLE',
      location_type: 'owned',
      address: placeData.address ?? null,
      phone: placeData.phone ?? null,
      website: placeData.website ?? null,
      provider_metadata: {
        google_maps_url: placeData.googleMapsUrl,
        latitude: placeData.latitude,
        longitude: placeData.longitude,
        types: placeData.types ?? [],
      },
    }),
  })
  return CreateInternalLocationResponseSchema.parse(data)
}

/**
 * Fetch internal locations
 */
export async function fetchInternalLocations(trackedOnly: boolean = false): Promise<Location[]> {
  const params = trackedOnly ? '?tracked_only=true' : ''
  const data = await apiFetch<Location[]>(`/locations/internal${params}`)
  const locations = z.array(LocationSchema).parse(data)
  return locations.filter((loc) => loc.location_type === 'owned')
}
